import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, ArrowRight } from "lucide-react";
import blogPosts from "../data/blogPosts";
import { useHeadMeta } from "../hooks/useHeadMeta";
import SocialMediaIcons from "./SocialMediaIcons";
import { gtmPush } from "../utils/gtm";

function BlogDetail() {
  const { slug } = useParams();

  const post = useMemo(
    () => blogPosts.find((item) => item.slug === slug) || blogPosts[0],
    [slug],
  );

  useHeadMeta({
    title: post.seoTitle,
    description: post.description,
    image: post.image,
    url: `https://www.srilatharakesh.com/#/blog/${post.slug}`,
    type: "article",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.description,
      image: post.image,
      author: {
        "@type": "Person",
        name: "Srilatha Rakesh",
      },
      publisher: {
        "@type": "Organization",
        name: "Srilatha Rakesh",
      },
      datePublished: post.date,
      mainEntityOfPage: `https://www.srilatharakesh.com/#/blog/${post.slug}`,
      articleSection: post.category,
      keywords: [
        post.category,
        "life coaching",
        "mental wellness",
        "self growth",
      ],
    },
  });

  const handleConsultationClick = () => {
    gtmPush("blog_consultation_click", {
      blog_title: post.title,
      blog_slug: post.slug,
      destination: "/contact",
      element: "book_consultation",
    });
  };

  const handleBackToBlogClick = () => {
    gtmPush("blog_back_click", {
      blog_title: post.title,
      blog_slug: post.slug,
      destination: "/blog",
      element: "back_to_blogs",
    });
  };

  return (
    <div className="bg-gray-50 min-h-screen py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/blog"
          onClick={handleBackToBlogClick}
          className="inline-flex items-center text-purple-700 font-semibold mb-8 hover:text-purple-800"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Blogs
        </Link>

        <article className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-72 md:h-[420px] object-cover"
          />

          <div className="p-6 md:p-10">
            <div className="mb-5">
              <span className="inline-block bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-semibold">
                {post.category}
              </span>
            </div>

            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center text-sm text-gray-500 mb-8 gap-4">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                <span>{new Date(post.date).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-2" />
                <span>{post.readTime}</span>
              </div>
            </div>

            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              {post.intro}
            </p>

            {post.sections.map((section, index) => (
              <div key={index} className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((paragraph, pIndex) => (
                  <p
                    key={pIndex}
                    className="text-lg text-gray-700 leading-8 mb-4"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            ))}

            <div className="bg-purple-50 border-l-4 border-purple-600 rounded-r-xl p-6 mt-10">
              <p className="text-lg text-gray-800 font-medium leading-8">
                {post.takeaway}
              </p>
            </div>

            <div className="mt-10 pt-8 border-t border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Need support?
              </h3>
              <p className="text-lg text-gray-700 mb-6">
                If this article resonates with you, we are here to help you take
                the next step with compassionate, practical support.
              </p>
              <Link
                to="/contact"
                onClick={handleConsultationClick}
                className="inline-flex items-center bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
              >
                Book a Consultation
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </article>
      </div>

      <SocialMediaIcons />
    </div>
  );
}

export default BlogDetail;
